/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Col, Dropdown, Grid, Input, Menu, Row, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import BotDeploymentsTable from '../components/Tables/BotDeploymentsTable';
import BotDeploymentsCloneModal from '../components/Modals/BotDeploymentsCloneModal';
import BotDeploymentsWebchatForm from '../components/Forms/BotDeploymentsWebchatForm';
import BotDeploymentsMsTeamsForm from '../components/Forms/BotDeploymentsMsTeamsForm';
import BotDeploymentsViberForm from '../components/Forms/BotDeploymentsViberForm';
import BotDeploymentsFacebookForm from '../components/Forms/BotDeploymentsFacebookForm';
import BotDeploymentsUnityForm from '../components/Forms/BotDeploymentsUnityForm';
import WebchatDeploymentSnippetModalModal from '../components/Modals/WebchatDeploymentSnippetModal';
import BotDeploymentsInstagramForm from '../components/Forms/BotDeploymentsInstagramForm';
import BotDeploymentsSlackForm from '../components/Forms/BotDeploymentsSlackForm';
import BotDeploymentsWhatsAppForm from '../components/Forms/BotDeploymentsWhatsAppForm';
import BotDeploymentsApiForm from '../components/Forms/BotDeploymentsApiForm';
import SideModal from '../components/Layout/SideModal';
import DeleteConfirmationModal from '../components/Modals/DeleteConfirmationModal';
import { SearchIcon } from '../components/Icons/SearchIcon';
import { PlusIcon } from '../components/Icons/PlusIcon';
import { ArrowDownIcon } from '../components/Icons/ArrowDownIcon';

// Misc imports
import { BOT_DEPLOYMENTS_CHANNELS, DEFAULT_DELETE_CONFIRMATION_KEYWORD } from '../constants/main';
import { PERMISSIONS_GROUP_ADMIN } from '../constants/main';
import { useCloneBotDeploymentMutation, useCreateBotDeploymentMutation, useDeleteBotDeploymentMutation, useFetchBotDeploymentsQuery, useUpdateBotDeploymentMutation } from '../store/botDeployments/botDeploymentsApi';
import { NAV_BOT, NAV_BOT_PREVIEW, NAV_EXTERNAL_URL_FACEBOOK_MESSENGER_CONVERSATION } from '../constants/navigation';
import useUserPermissions from '../utils/hooks/useUserPermissions';
import replaceUrlParams from '../utils/string/replaceUrlParams';
import userHasEditAccess from '../utils/misc/hasEditAccess';
import openConsoleAlertMessage from '../utils/misc/openConsoleAlertMessage';

// Constant declarations
export const PANEL_KEYS = {
  LIST: 'list',
  EDIT: 'edit'
};

const { useBreakpoint } = Grid;

const BotDeploymentsScreen = ({
  isInitialScreen = false,
  handleBreadCrumbs = () => { }
}) => {
  const { tenantId, orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobileSize = screens.xs && !screens.sm;
  const isMdSize = screens.md && !screens.lg;
  const isSmSize = screens.sm && !screens.lg;
  const isTabletSize = (screens.xs || screens.md || screens.sm) && !screens.lg;
  const userPermissions = useUserPermissions(orgId, tenantId);
  const canEdit = userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission));

  const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);
  const [searchParams] = useSearchParams({});

  // States
  const [filteredInfo, setFilteredInfo] = useState({ search: '' });
  const [sortedInfo, setSortedInfo] = useState({ order: 'ascend', columnKey: 'displayName' });
  const [isCloneDeploymentModalOpen, setIsCloneDeploymentModalOpen] = useState(false);
  const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
  const [deploymentToClone, setDeploymentToClone] = useState({});
  const [deploymentToEdit, setDeploymentToEdit] = useState({});
  const [deploymentToDelete, setDeploymentToDelete] = useState();
  const [deploymentToCreateSnippet, setDeploymentToCreateSnippet] = useState({});
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const isAdmin = userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles, true);

  const { data: botDeployments, isFetching: isLoadingBotDeployments } = useFetchBotDeploymentsQuery({ orgId, tenantId });
  const [updateBotDeployment, { isLoading: isUpdatingBotDeployment }] = useUpdateBotDeploymentMutation();
  const [createBotDeployment, { isLoading: isCreatingBotDeployment }] = useCreateBotDeploymentMutation();
  const [cloneBotDeployment, { isLoading: isCloningBotDeployment }] = useCloneBotDeploymentMutation();
  const [deleteBotDeployment, { isLoading: isDeletingBotDeployment }] = useDeleteBotDeploymentMutation();

  // Generic Handlers
  const handleGlobalSearch = value => {
    setFilteredInfo(
      {
        ...filteredInfo,
        search: value
      }
    );
  };

  const navigateToCreateDeploymentForm = (platform) => {
    searchParams.set('deployment', 'create-deployment');
    searchParams.set('platform', platform);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  const handleBulkActionsDropdownClick = (e) => {
    navigateToCreateDeploymentForm(e.key);
  };

  // Navigate to specific deployment view
  const navigateToDeployment = (deploymentToNavigate) => {
    searchParams.set('deployment', deploymentToNavigate.id);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  // Clone Deployment Modal Handlers
  const closeCloneDeploymentModal = () => {
    setIsCloneDeploymentModalOpen(false);
    setDeploymentToClone({});
  };

  const handleCloneDeployment = async (values) => {
    const isCloningSuccessful = await cloneBotDeployment({ orgId, tenantId, deploymentId: deploymentToClone.id, data: values }).unwrap();

    if (isCloningSuccessful) {
      openConsoleAlertMessage({
        type: 'success',
        message: t('Agent Deployment cloned'),
        duration: 10,
        secondaryButton: {
          action: () => {
            navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: values.tenantId || tenantId })}?view=deployments&contentLang=en&deployment=${isCloningSuccessful}`);
          },
          title: t('View')
        }
      });
      closeCloneDeploymentModal();
    } else {
      openConsoleAlertMessage({ type: 'error', message: t('There was an error cloning the flow.') });
    }
  };

  const handleBotDeploymentsDeleteDeployment = async () => {
    const isDeploymentDeleted = await deleteBotDeployment({ orgId, tenantId, deploymentId: deploymentToDelete }).unwrap();
    if(isDeploymentDeleted) {
      handleBotDeploymentsDeleteModalClose();
      closeDeploymentModal();
    }
  };

  const handleBotDeploymentsDeleteModalOpen = (deploymentId) => {
    setIsDeleteModalOpen(prev => {
      setDeploymentToDelete(deploymentId);
      return true;
    });
  };

  const handleBotDeploymentsDeleteModalClose = () => {
    setIsDeleteModalOpen(prev => {
      setDeploymentToDelete(null);
      return false;
    });
  };

  // Snippet Modal Handlers
  const handleSnippetIconModal = (deployment) => {
    setIsSnippetModalOpen(true);
    setDeploymentToCreateSnippet(deployment);
  };

  const handleCloseSnippetModal = () => {
    setIsSnippetModalOpen(false);
  };

  // Bot Deployment Table Handlers
  const handleBotDeploymentsPreviewDeployment = (deployment) => {
    let previewUrl = '';

    if(deployment.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key) {
      previewUrl = replaceUrlParams(NAV_BOT_PREVIEW, { tenantId: deployment.tenant.id })
      +`?deploymentId=${encodeURIComponent(deployment.id)}`
      +`&name=${encodeURIComponent(deployment.tenant.displayName)}`
      +`&description=${encodeURIComponent(deployment.tenant.description)}`
      +`&analytics=true`
      +`&metadata=`;
    }

    if(deployment.platform === BOT_DEPLOYMENTS_CHANNELS.FACEBOOK.key) {
      const pageId = deployment?.settings?.integration?.facebook?.pageId;
      previewUrl = `${NAV_EXTERNAL_URL_FACEBOOK_MESSENGER_CONVERSATION}${pageId}`;
    }

    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleBotDeploymentsTableCloneDeployment = (deployment) => {
    setIsCloneDeploymentModalOpen(true);
    setDeploymentToClone(deployment);
  };

  const handleBotDeploymentsTableEditDeployment = (props) => {
    setDeploymentToEdit(props);
    navigateToDeployment(props);
  };

  const handleBotDeploymentsTableChange = (_pagination, _filters, sorter) => {
    setSortedInfo(sorter);
  };

  const closeDeploymentModal = () => {
    searchParams.delete('deployment');
    searchParams.delete('platform');
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  // Form handlers
  const handleBotDeploymentsSubmit = async (data, id = '', isCreateMode) => {
    if (!isCreateMode) {
      updateBotDeployment({ orgId, tenantId, deploymentId: id, data: data });
    } else {
      const isCreateSuccessful = await createBotDeployment({ orgId, tenantId, data: data }).unwrap();
      if (isCreateSuccessful) {
        closeDeploymentModal();
      }
    }
  };

  // Render Content
  const renderDeploymentForm = () => {
    // Check the conditions to render the correct platform's form and if it's create or edit mode
    let deployment = {};
    let isCreateMode = false;

    if (searchParams.has('deployment')) {
      if (searchParams.get('deployment') === 'create-deployment') {
        isCreateMode = true;
        deployment['platform'] = searchParams.get('platform');
      } else {
        deployment = botDeployments?.items?.filter((deployment) => deployment.id === searchParams.get('deployment'))[0];
      }
    } else {
      deployment = deploymentToEdit;
    }

    switch (deployment?.platform) {
      case BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key:
        return (
          <BotDeploymentsWebchatForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            onSnippetIconClick={handleSnippetIconModal}
            onPreviewIconClick={handleBotDeploymentsPreviewDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_TEAMS.key:
        return (
          <BotDeploymentsMsTeamsForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.VIBER.key:
        return (
          <BotDeploymentsViberForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.FACEBOOK.key:
        return (
          <BotDeploymentsFacebookForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            onPreviewIconClick={handleBotDeploymentsPreviewDeployment}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.UNITY.key:
        return (
          <BotDeploymentsUnityForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.API.key:
        return (
          <BotDeploymentsApiForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.SLACK.key:
        return (
          <BotDeploymentsSlackForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.INSTAGRAM.key:
        return (
          <BotDeploymentsInstagramForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      case BOT_DEPLOYMENTS_CHANNELS.WHATSAPP.key:
        return (
          <BotDeploymentsWhatsAppForm
            onFormSubmit={handleBotDeploymentsSubmit}
            formData={deployment}
            userRoles={{
              organizationRoles,
              tenantRoles
            }}
            isCreateMode={isCreateMode}
            isUpdating={isUpdatingBotDeployment}
            isCreating={isCreatingBotDeployment}
            onDeleteClick={handleBotDeploymentsDeleteModalOpen}
            handleBackButton={closeDeploymentModal}
            bodyClassName="px-6 pt-6 flex-grow-1"
            footerClassName="ant-drawer-footer"
          />
        );

      default:
        break;
    }
  };

  const renderInitialScreen = () => {
    return (
      <Fragment>
        <Row>
          <Col span={24}>
            <Typography.Paragraph className="fs-xl mb-1">
              {t('Connect your agent to your channels')}
            </Typography.Paragraph>
          </Col>
          <Col span={24}>
            <Typography.Paragraph className="fs-lg text-muted">
              {t('Engage with your audience across all platforms')}
            </Typography.Paragraph>
          </Col>
        </Row>

        <Row
          gutter={16}
          className={classNames({ 'flex-column': isMobileSize, 'flex-row': !isMobileSize })}
        >
          {
            Object.entries(BOT_DEPLOYMENTS_CHANNELS).map(([key, value]) => {
              return (
                <Col key={key} span={isMobileSize ? 24 : isMdSize || isSmSize ? 12 : 8}>
                  <Card className="mb-5">
                    <Typography.Paragraph
                      className="mb-1">
                      <span className="fs-xl mr-1">
                        {value.icon}
                      </span>
                      <span className="fs-xl">
                        {value.name}
                      </span>
                    </Typography.Paragraph>

                    <Typography.Paragraph className="text-secondary mt-1 mb-5 fs-base">
                      {value.description}
                    </Typography.Paragraph>

                    <Button
                      size="large"
                      disabled={!canEdit}
                      className="float-right mt-3"
                      onClick={() => {
                        navigateToCreateDeploymentForm(key);
                      }}
                    >
                      {t('Deploy')}
                    </Button>
                  </Card>
                </Col>
              );
            })
          }
        </Row>
      </Fragment>
    );
  };

  const renderDeployButtons = () => {
    return (
      <Fragment>
        {
          isTabletSize ? (
            <Fragment>
              <Col hidden={isSearchActive || !isAdmin} span={isTabletSize ? (isMobileSize && !isSmSize ? 11 : 8) : 6}>
                <Dropdown
                  trigger={['click']}
                  overlay={
                    <Menu
                      className="fs-xxl"
                      onClick={handleBulkActionsDropdownClick}
                      items={Object.entries(BOT_DEPLOYMENTS_CHANNELS).map(([key, value]) => {
                        return {
                          key: key,
                          label: <Typography.Text className="fs-lg">
                            <span className="mr-1">{value.icon}</span>
                            <span>{value.name}</span>
                          </Typography.Text>,
                          disabled: !canEdit
                        };
                      })}
                    />
                  }
                >
                  <Button
                    className="float-right"
                    size="large"
                    block
                  >
                    <PlusIcon />{t('Deployment')}<ArrowDownIcon />
                  </Button>
                </Dropdown>
              </Col>
            </Fragment>
          ) : (
            <Fragment>
              {
                Object.entries(BOT_DEPLOYMENTS_CHANNELS).map(([key, value]) => {
                  return (
                    <Col hidden={!isAdmin} key={key}>
                      <Button
                        className="float-right d-flex align-items-center border-0 shadow"
                        size="large"
                        onClick={() => {
                          navigateToCreateDeploymentForm(key);
                        }}
                      >
                        <PlusIcon className="text-secondary fs-lg" />  <span className="fs-xl ml-0">{value.icon}</span>
                      </Button>
                    </Col>
                  );
                })
              }
            </Fragment>
          )
        }
      </Fragment>
    );
  };

  const renderInputSearch = () => {
    return (
      <Fragment>
        {
          isMobileSize && !isMdSize && isAdmin ? (
            <Fragment>
              <Col span={3} hidden={isSearchActive}>
                <span onClick={() => {
                  setIsSearchActive(true);
                }}>
                  <SearchIcon className="fs-xxl" />
                </span>
              </Col>

              <Col span={24} hidden={!isSearchActive}>
                <Input
                  prefix={
                    <SearchOutlined
                      onClick={() => {
                        setIsSearchActive(false);
                      }}
                      className="text-secondary"
                    />
                  }
                  size="large"
                  placeholder={t('Search in contents')}
                  allowClear
                  disabled={isLoadingBotDeployments}
                  onChange={(event) => handleGlobalSearch(event.target.value)}
                />
              </Col>
            </Fragment>
          ) : (
            <Col flex={isTabletSize ? '' : 'auto'} span={isTabletSize ? (isAdmin ? 8 : 24) : 18}>
              <Space className="flex-wrap mb-2">
                <Input
                  prefix={<SearchOutlined className="text-secondary" />}
                  size="large"
                  placeholder={t('Search in contents')}
                  allowClear
                  disabled={isLoadingBotDeployments}
                  onChange={(event) => handleGlobalSearch(event.target.value)}
                />
              </Space>
            </Col>
          )
        }
      </Fragment>
    );
  };

  const renderActionsSections = () => {
    return (
      <Fragment>
        <Row hidden={!isTabletSize || !isAdmin} gutter={16}>
          <Col span={24}>
            <Space className="flex-wrap mb-2 float-right">
              <Typography.Text className="fs-xs text-muted">
                {t('supported channels')}:
                <br />
                <span className="float-right">
                  {
                    Object.entries(BOT_DEPLOYMENTS_CHANNELS).map(([key, value], index) => {
                      return <span key={key} className={index !== BOT_DEPLOYMENTS_CHANNELS.length - 1 ? 'mr-2' : ''}>{value.icon}</span>;
                    })
                  }
                </span>
              </Typography.Text>
            </Space>
          </Col>
        </Row>

        <Row
          gutter={16}
          className={classNames('mb-2', { 'justify-content-between': isTabletSize || isMobileSize })}
        >
          {renderInputSearch()}
          {renderDeployButtons()}
        </Row>
      </Fragment>
    );
  };

  return (
    <Fragment>
      {
        !isInitialScreen ? (
          <Fragment>
            {
              renderActionsSections()
            }
            <Row gutter={24}>
              <Col xs={24}>
                {
                  botDeployments?.items?.length > 0 ? (
                    <BotDeploymentsTable
                      botDeployments={botDeployments.items}
                      loader={isLoadingBotDeployments || isCloningBotDeployment || isDeletingBotDeployment}
                      filteredData={filteredInfo}
                      sortedData={sortedInfo}
                      hasEditAccess={isAdmin}
                      handlePreviewDeployment={handleBotDeploymentsPreviewDeployment}
                      handleEditDeployment={handleBotDeploymentsTableEditDeployment}
                      handleTableChange={handleBotDeploymentsTableChange}
                      handleCloneDeployment={handleBotDeploymentsTableCloneDeployment}
                      handleSnippetIconModal={handleSnippetIconModal}
                    />
                  ) : null
                }

              </Col>
            </Row>

            <BotDeploymentsCloneModal
              isModalOpen={isCloneDeploymentModalOpen}
              onModalClose={closeCloneDeploymentModal}
              deploymentToClone={deploymentToClone}
              loader={isLoadingBotDeployments}
              onModalConfirm={handleCloneDeployment}
            />

            <DeleteConfirmationModal
              title={t('Delete Deployment Forever?')}
              onDelete={handleBotDeploymentsDeleteDeployment}
              isDeleting={isDeletingBotDeployment}
              isModalOpen={isDeleteModalOpen}
              closeModal={handleBotDeploymentsDeleteModalClose}
              showDeleteKeyword
              deleteKeyword={DEFAULT_DELETE_CONFIRMATION_KEYWORD.DEPLOYMENT}
              zIndex={1030}
            />
          </Fragment>
        ) : (
          renderInitialScreen()
        )
      }

      <SideModal
        className="deployments-side-modal"
        open={searchParams.has('deployment')}
        onClose={closeDeploymentModal}
        title={t('Agent Deployment')}
        description={t('Create or edit an agent deployment')}
        bodyClassName="p-0"
        destroyOnClose
      >
        {renderDeploymentForm()}
      </SideModal>

      <WebchatDeploymentSnippetModalModal
        isModalOpen={isSnippetModalOpen}
        deploymentToCopySnippet={deploymentToCreateSnippet}
        loader={isLoadingBotDeployments}
        onModalClose={handleCloseSnippetModal}
      />
    </Fragment>
  );
};

BotDeploymentsScreen.propTypes = {
  isInitialScreen: PropTypes.bool,
  handleBreadCrumbs: PropTypes.func
};

export default React.memo(BotDeploymentsScreen);
